import React from 'react';
import PropTypes from 'prop-types';

import {Box, CardContent, Divider, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {StageDropdown} from './dropdowns';
import {CallStatusPill} from './pills';

const CardBody = (props) => {
  const {
    callStatus,
    caregiverCount,
    clientCreatedAtLabel,
    clientDaysInStageText,
    clientId,
    clientLocation,
    clientName,
    leadQuality,
    notSetLabel,
    onStageChanged,
    onStageOverlayChange,
    primaryRelationship,
    status,
  } = props;

  return (
    <CardContent
      sx={{
        p: '20px !important',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Stack
        sx={{
          height: '100%',
          justifyContent: 'space-between',
          minWidth: 0,
        }}
      >
        <Stack spacing={1.5} sx={{minWidth: 0, pb: 1.5}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  component='div'
                  sx={{
                    color: '#1A1916',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '21px',
                    minWidth: 0,
                  }}
                >
                  {clientName}
                </Typography>
              </Box>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  mt: '4px',
                  wordBreak: 'break-word',
                }}
              >
                {primaryRelationship?.name || notSetLabel}
              </Typography>
              {
                primaryRelationship?.connectionType &&
                <Typography
                  component='div'
                  sx={{
                    color: '#A8A49E',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    wordBreak: 'break-word',
                  }}
                >
                  ({primaryRelationship.connectionType})
                </Typography>
              }
            </Box>
            <Stack
              spacing={0.75}
              sx={{
                alignItems: 'flex-end',
                flexShrink: 0,
              }}
            >
              <StageDropdown
                clientId={clientId}
                leadQuality={leadQuality}
                selectedStage={status}
                onChanged={onStageChanged}
                onOverlayChange={onStageOverlayChange}
              />
              {
                callStatus &&
                <CallStatusPill callStatus={callStatus} />
              }
            </Stack>
          </Box>

          <Divider sx={{borderColor: '#EBEBEB'}} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              columnGap: 2,
              rowGap: 2,
            }}
          >
            <Box>
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='loomLead.LoomLeadList.Card.Subtitles.DaysInStage' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                }}
              >
                <Translate
                  expression='loomLead.LoomLeadList.Card.Subtitles.InStage'
                  substitute={{value: clientDaysInStageText}}
                />
              </Typography>
            </Box>

            <Box
              sx={{
                ml: {
                  xs: 0, md: 4, lg: 8,
                },
              }}
            >
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='loomLead.LoomLeadList.Card.Subtitles.Location' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: clientLocation === notSetLabel ? '#A8A49E' : '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                  wordBreak: 'break-word',
                }}
              >
                {clientLocation}
              </Typography>
            </Box>

            <Box>
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='loomLead.LoomLeadList.Card.Subtitles.Created' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                }}
              >
                {clientCreatedAtLabel}
              </Typography>
            </Box>

            <Box
              sx={{
                ml: {
                  xs: 0, md: 4, lg: 8,
                },
              }}
            >
              <Typography
                component='div'
                sx={{
                  color: '#A8A49E',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: '14px',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='loomLead.LoomLeadList.Card.Subtitles.Caregivers' />
              </Typography>
              <Typography
                component='div'
                sx={{
                  color: '#1A1916',
                  fontSize: '11px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  mt: '4px',
                }}
              >
                {caregiverCount}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{borderColor: '#EBEBEB'}} />
        </Stack>
      </Stack>
    </CardContent>
  );
};

CardBody.propTypes = {
  callStatus: PropTypes.string,
  caregiverCount: PropTypes.number.isRequired,
  clientCreatedAtLabel: PropTypes.string.isRequired,
  clientDaysInStageText: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  clientLocation: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  leadQuality: PropTypes.string,
  notSetLabel: PropTypes.string.isRequired,
  onStageChanged: PropTypes.func.isRequired,
  onStageOverlayChange: PropTypes.func.isRequired,
  primaryRelationship: PropTypes.object,
  status: PropTypes.string.isRequired,
};

export default React.memo(CardBody);
